/**
 * The Power and Energy preview, at `/power` (components/power/,
 * docs/foundations.md §8).
 *
 * Quantities arrive formatted (`q`, `pct`), as in foundations.ts. The compass
 * points and month initials are the reader's own; units are symbols.
 */
import type { CityId, Facing } from '../power';

const PLURAL_AR_PANELS = (n: number): string => {
  if (n === 1) return 'لوح واحد';
  if (n === 2) return 'لوحان';
  if (n >= 3 && n <= 10) return `${n} ألواح`;
  return `${n} لوحًا`;
};

/* ── English ──────────────────────────────────────────────────────────────── */

export const en = {
  docTitle: 'Power and Energy preview · EngineerOS',
  kicker: 'A preview of power and energy engineering',
  title: 'A solar roof',
  lede:
    "Five tasks on one solar roof: point the panels, size the array, keep the lights on overnight, get the power into the " +
    "house, and check Iris's plan for a colder city.",
  honest:
    'A preview, with models simplified on purpose; every step says what it leaves out. A real installation is designed and ' +
    'connected by a qualified electrician.',
  progress: (done: number, total: number) => `${done} of ${total} done`,
  finish: {
    title: 'You finished the Power and Energy preview',
    lede: 'Five decisions, each one tested. If you want the full course, say so below: it is how we decide what to build next.',
  },

  steps: {
    tilt: {
      idea: 'The sun',
      title: 'Point the panel',
      story:
        'A panel catches the most light when it faces the midday sun, and the midday sun sits toward the equator, lower the ' +
        'further you are from it. Pick a city and point a panel there.',
      task: 'Choose which way the panel faces and how far it tilts. Catch at least 97% of the best this city allows.',
      debrief:
        'A good rule: face the equator and tilt by about the latitude. In Cairo that is south at 30°; in Sydney it is north; ' +
        'in Nairobi, on the equator, nearly flat. Try another city: the rule holds.',
      leavesOut: 'Clouds, dust, heat (panels lose power when hot) and shade. This is a clear-sky model, used only to compare angles.',
    },
    array: {
      idea: 'Supply and demand',
      title: 'How many panels?',
      story:
        'A Cairo home uses about 6,100 kWh a year, most of it in summer for air conditioning. Each panel is 400 W, and the roof ' +
        'has room for 14.',
      task: "Click the roof to place panels. Cover between 80% and 120% of the year's use: less leaves big bills, more sells power back for little.",
      debrief:
        'Seven to ten panels do it. In Cairo the sun and the air conditioning peak together in summer, a lucky match. In a colder ' +
        'city the two pull apart, as the last step shows.',
      leavesOut: 'Day against night (the next step), cloudy days, panels ageing about half a percent a year, and what the grid pays for power sent back.',
    },
    battery: {
      idea: 'Storage',
      title: 'Keep the lights on overnight',
      story:
        "The sun sets at six, and the home uses 6 kWh between sunset and sunrise, most of it in the evening. A battery can carry " +
        "the night, but only 90% of it should be used, and the owner's budget is $4,000 at $450 per kWh.",
      task: 'Choose a battery that lasts until sunrise and fits the budget.',
      debrief:
        "Only 7.5 kWh works: 6.75 usable, enough for the night, for $3,375. Batteries are sized for the hardest night, and the " +
        "90% rule is the battery's own safety margin: draining it flat wears it out faster.",
      leavesOut: 'Winter nights, about a tenth lost charging and discharging, the battery fading with age, and a cloudy day that never fills it.',
    },
    cable: {
      idea: 'Wires',
      title: 'The cable to the house',
      story:
        'The inverter is 25 m from the main board and sends 25 A at 230 V. Every cable has resistance: too thin and it runs hot, ' +
        'and the voltage that reaches the house drops. The rule here: lose no more than 3%.',
      task: 'Choose the thinnest copper cable that carries 25 A safely and loses no more than 3% of the voltage.',
      debrief:
        "4 mm² is the thinnest that passes: rated for 32 A, it loses about 2.3%. It is the Iron Man step from Foundations at a " +
        'home\'s scale: current heats wires and wastes power, so cables are sized for both.',
      leavesOut: 'How the cable is laid (inside a wall or in open air changes its rating), temperature, and the protection at each end.',
    },
    'power-ai': {
      idea: 'Checking the AI',
      title: 'Iris moves the roof to Berlin',
      story: 'A second client lives in Berlin and uses about 3,900 kWh a year, more in winter. Iris has copied the Cairo plan.',
      task: "Test Iris's plan against Berlin's months. If it covers less than 80% of the year, fix it with panels or tilt.",
      debrief:
        "Iris copied Cairo's sun. A panel in Berlin makes little more than half what it makes in Cairo, and Berlin's use peaks " +
        'in winter, when the sun is lowest. A plan can move between cities only after its numbers do.',
      leavesOut: "Snow on the panels, Berlin's grey winters (the monthly shape is clear-sky), and the steeper tilt that would favour winter.",
    },
  },

  cities: { cairo: 'Cairo', berlin: 'Berlin', tokyo: 'Tokyo', nairobi: 'Nairobi', sydney: 'Sydney' } as Record<CityId, string>,
  compass: { N: 'N', NE: 'NE', E: 'E', SE: 'SE', S: 'S', SW: 'SW', W: 'W', NW: 'NW' } as Record<Facing, string>,
  months: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  tilt: {
    city: 'City',
    facing: 'Faces',
    tilt: 'Tilt',
    unknown: 'Press Check to see how much light it catches',
    catches: (pct: string) => `Catches ${pct} of the best possible`,
    ok: (pct: string) => `Well pointed: ${pct} of the best.`,
    fail: (pct: string, target: string) => `Only ${pct} of the best. Aim for ${target}.`,
  },
  array: {
    roof: 'Roof, room for 14 panels',
    slot: (n: number) => `Panel space ${n}`,
    made: (q: string) => `Makes ${q} a year`,
    used: (q: string) => `uses ${q}`,
    count: (n: number) => (n === 1 ? '1 panel' : `${n} panels`),
    legend: 'Solid bars: made by the panels. Outlined: used by the home.',
    ok: (pct: string) => `Covers ${pct} of the year.`,
    short: (pct: string, min: string) => `Covers only ${pct}. Aim for at least ${min}.`,
    over: (pct: string, max: string) => `${pct} is more than the home needs. Stay under ${max}.`,
    roofFull: 'The roof has room for 14.',
  },
  battery: {
    size: 'Battery',
    usable: (q: string, share: string) => `${q} usable (${share})`,
    need: (q: string) => `the night needs ${q}`,
    cost: (q: string, budget: string) => `${q} of ${budget}`,
    price: (q: string) => `${q} per kWh`,
    ok: 'The lights stay on until sunrise, within budget.',
    short: (h: string) => `It runs flat after ${h}. The house goes dark before dawn.`,
    budget: (cost: string, budget: string) => `It lasts the night, but ${cost} is over the ${budget} budget.`,
  },
  cable: {
    size: 'Cable',
    dropRead: (pct: string) => `Voltage drop ${pct}`,
    drop: (pct: string) => `The house loses ${pct} of its voltage. The limit is 3%.`,
    rating: (q: string) => `rated ${q}`,
    ok: (pct: string) => `Safe, and only ${pct} lost on the way.`,
    hot: (rating: string, need: string) => `Rated ${rating}, carrying ${need}: it overheats.`,
  },
  iris: 'Use the Cairo plan: 8 panels at 30°. It covered more than 90% there, so Berlin will be fine.',
};

/* ── Arabic ───────────────────────────────────────────────────────────────── */

export const ar: typeof en = {
  docTitle: 'معاينة الطاقة · EngineerOS',
  kicker: 'معاينة لهندسة الطاقة والقوى الكهربائية',
  title: 'سطح شمسي',
  lede: 'خمس مهام على سطح شمسي واحد: وجّه الألواح، وحدّد عددها، وأبقِ الأضواء طوال الليل، وأوصل الكهرباء إلى البيت، وتحقّق من خطة Iris لمدينة أبرد.',
  honest: 'معاينة، والنماذج فيها مبسّطة عن قصد؛ كل خطوة تقول ما تتركه جانبًا. التركيب الحقيقي يصمّمه ويوصله كهربائي مؤهّل.',
  progress: (done: number, total: number) => `أنجزت ${done} من ${total}`,
  finish: {
    title: 'أتممت معاينة الطاقة',
    lede: 'خمسة قرارات، اختُبر كل واحد منها. إن أردت المسار الكامل فقل ذلك أدناه: هكذا نقرّر ما نبنيه بعد ذلك.',
  },

  steps: {
    tilt: {
      idea: 'الشمس',
      title: 'وجّه اللوح',
      story: 'يلتقط اللوح أكبر قدر من الضوء حين يواجه شمس الظهيرة، وشمس الظهيرة تميل نحو خط الاستواء، وتنخفض كلما ابتعدت عنه. اختر مدينة ووجّه لوحًا فيها.',
      task: 'اختر الاتجاه الذي يواجهه اللوح ومقدار ميله. التقط 97% على الأقل من أفضل ما تسمح به هذه المدينة.',
      debrief:
        'قاعدة جيدة: واجه خط الاستواء وأمِل اللوح بقدر خط العرض تقريبًا. في القاهرة يعني ذلك الجنوب بزاوية 30°، وفي سيدني الشمال، ' +
        'وفي نيروبي على خط الاستواء يكاد يكون مسطّحًا. جرّب مدينة أخرى: القاعدة صحيحة.',
      leavesOut: 'الغيوم والغبار والحرارة (الألواح تفقد قدرتها حين تسخن) والظل. هذا نموذج سماء صافية، يُستعمل للمقارنة بين الزوايا فقط.',
    },
    array: {
      idea: 'العرض والطلب',
      title: 'كم لوحًا؟',
      story: 'بيت في القاهرة يستهلك نحو 6,100 كيلوواط ساعة في السنة، معظمها في الصيف للتكييف. قدرة كل لوح 400 واط، والسطح يتّسع لـ 14.',
      task: 'انقر السطح لتضع الألواح. غطِّ ما بين 80% و120% من استهلاك السنة: الأقل يترك فواتير كبيرة، والأكثر يبيع الكهرباء بثمن زهيد.',
      debrief: 'سبعة إلى عشرة ألواح تكفي. في القاهرة تبلغ الشمس والتكييف ذروتهما معًا في الصيف، وهو توافق محظوظ. وفي مدينة أبرد يفترقان، كما تبيّن الخطوة الأخيرة.',
      leavesOut: 'النهار مقابل الليل (الخطوة التالية)، والأيام الغائمة، وتقادم الألواح بنحو نصف بالمئة سنويًا، وما تدفعه الشبكة مقابل الكهرباء المصدّرة.',
    },
    battery: {
      idea: 'التخزين',
      title: 'أبقِ الأضواء طوال الليل',
      story:
        'تغرب الشمس في السادسة، ويستهلك البيت 6 كيلوواط ساعة بين الغروب والشروق، معظمها في المساء. البطارية تستطيع حمل الليل، لكن لا ينبغي ' +
        'استعمال إلا 90% منها، وميزانية صاحب البيت 4,000 دولار بسعر 450 دولارًا لكل كيلوواط ساعة.',
      task: 'اختر بطارية تدوم حتى الشروق وتناسب الميزانية.',
      debrief:
        'وحدها 7.5 كيلوواط ساعة تنجح: 6.75 قابلة للاستعمال، تكفي الليل، بسعر 3,375 دولارًا. تُحدَّد البطاريات على أصعب ليلة، وقاعدة 90% ' +
        'هي هامش أمان البطارية نفسها: تفريغها حتى النهاية يُبليها أسرع.',
      leavesOut: 'ليالي الشتاء، ونحو عُشر الطاقة الضائع في الشحن والتفريغ، وتراجع البطارية مع العمر، ويوم غائم لا يملؤها أبدًا.',
    },
    cable: {
      idea: 'الأسلاك',
      title: 'الكبل إلى البيت',
      story:
        'العاكس على بعد 25 م من اللوحة الرئيسية ويرسل 25 أمبير بجهد 230 فولت. لكل كبل مقاومة: إن كان رفيعًا جدًا سخن، وانخفض الجهد ' +
        'الذي يصل إلى البيت. القاعدة هنا: لا تخسر أكثر من 3%.',
      task: 'اختر أرقّ كبل نحاسي يحمل 25 أمبير بأمان ولا يخسر أكثر من 3% من الجهد.',
      debrief:
        '4 مم² هو الأرقّ الذي ينجح: يحتمل 32 أمبير ويخسر نحو 2.3%. إنها خطوة Iron Man من الأساسيات على مقياس بيت: التيار يسخّن ' +
        'الأسلاك ويهدر الطاقة، فتُحدَّد الكبلات للأمرين معًا.',
      leavesOut: 'طريقة مدّ الكبل (داخل جدار أو في الهواء الطلق يغيّر قدرته)، والحرارة، وأجهزة الحماية في الطرفين.',
    },
    'power-ai': {
      idea: 'التحقّق من الذكاء الاصطناعي',
      title: 'Iris تنقل السطح إلى برلين',
      story: 'عميل ثانٍ يعيش في برلين ويستهلك نحو 3,900 كيلوواط ساعة في السنة، أكثرها في الشتاء. وقد نسخت Iris خطة القاهرة.',
      task: 'اختبر خطة Iris على أشهر برلين. إن غطّت أقل من 80% من السنة فأصلحها بالألواح أو بالميل.',
      debrief:
        'نسخت Iris شمس القاهرة. اللوح في برلين ينتج أكثر بقليل من نصف ما ينتجه في القاهرة، واستهلاك برلين يبلغ ذروته في الشتاء حين ' +
        'تكون الشمس في أدنى مستوياتها. لا تنتقل خطة بين المدن إلا بعد أن تنتقل أرقامها.',
      leavesOut: 'الثلج على الألواح، وشتاء برلين الرمادي (شكل الأشهر هنا لسماء صافية)، والميل الأشدّ الذي يناسب الشتاء.',
    },
  },

  cities: { cairo: 'القاهرة', berlin: 'برلين', tokyo: 'طوكيو', nairobi: 'نيروبي', sydney: 'سيدني' },
  compass: { N: 'ش', NE: 'ش ق', E: 'ق', SE: 'ج ق', S: 'ج', SW: 'ج غ', W: 'غ', NW: 'ش غ' },
  months: ['ينا', 'فبر', 'مار', 'أبر', 'ماي', 'يون', 'يول', 'أغس', 'سبت', 'أكت', 'نوف', 'ديس'],
  tilt: {
    city: 'المدينة',
    facing: 'يواجه',
    tilt: 'الميل',
    unknown: 'اضغط «تحقّق» لترى كم يلتقط من الضوء',
    catches: (pct: string) => `يلتقط ${pct} من أفضل ما يمكن`,
    ok: (pct: string) => `موجَّه جيدًا: ${pct} من الأفضل.`,
    fail: (pct: string, target: string) => `فقط ${pct} من الأفضل. استهدف ${target}.`,
  },
  array: {
    roof: 'السطح، يتّسع لـ 14 لوحًا',
    slot: (n: number) => `مكان اللوح ${n}`,
    made: (q: string) => `ينتج ${q} في السنة`,
    used: (q: string) => `يستهلك ${q}`,
    count: PLURAL_AR_PANELS,
    legend: 'الأعمدة المصمتة: ما تنتجه الألواح. المحدّدة: ما يستهلكه البيت.',
    ok: (pct: string) => `يغطّي ${pct} من السنة.`,
    short: (pct: string, min: string) => `يغطّي ${pct} فقط. استهدف ${min} على الأقل.`,
    over: (pct: string, max: string) => `${pct} أكثر مما يحتاجه البيت. ابقَ تحت ${max}.`,
    roofFull: 'السطح يتّسع لـ 14.',
  },
  battery: {
    size: 'البطارية',
    usable: (q: string, share: string) => `${q} قابلة للاستعمال (${share})`,
    need: (q: string) => `الليل يحتاج إلى ${q}`,
    cost: (q: string, budget: string) => `${q} من ${budget}`,
    price: (q: string) => `${q} لكل كيلوواط ساعة`,
    ok: 'تبقى الأضواء حتى الشروق، ضمن الميزانية.',
    short: (h: string) => `تنفد بعد ${h}. يُظلم البيت قبل الفجر.`,
    budget: (cost: string, budget: string) => `تكفي الليل، لكن ${cost} يتجاوز ميزانية ${budget}.`,
  },
  cable: {
    size: 'الكبل',
    dropRead: (pct: string) => `هبوط الجهد ${pct}`,
    drop: (pct: string) => `يخسر البيت ${pct} من جهده. الحدّ 3%.`,
    rating: (q: string) => `قدرته ${q}`,
    ok: (pct: string) => `آمن، ولا يضيع في الطريق إلا ${pct}.`,
    hot: (rating: string, need: string) => `قدرته ${rating} ويحمل ${need}: يسخن أكثر من اللازم.`,
  },
  iris: 'استعمل خطة القاهرة: 8 ألواح بزاوية 30°. غطّت هناك أكثر من 90%، فستكون برلين على ما يرام.',
};

/* ── German ───────────────────────────────────────────────────────────────── */

export const de: typeof en = {
  docTitle: 'Vorschau Energietechnik · EngineerOS',
  kicker: 'Eine Vorschau auf die Energietechnik',
  title: 'Ein Solardach',
  lede:
    'Fünf Aufgaben an einem Solardach: Module ausrichten, die Anlage bemessen, das Licht über Nacht anlassen, den Strom ins ' +
    'Haus bringen und den Plan von Iris für eine kältere Stadt prüfen.',
  honest:
    'Eine Vorschau mit absichtlich vereinfachten Modellen; jeder Schritt sagt, was er weglässt. Eine echte Anlage plant und ' +
    'schließt eine Elektrofachkraft an.',
  progress: (done: number, total: number) => `${done} von ${total} erledigt`,
  finish: {
    title: 'Du hast die Vorschau Energietechnik abgeschlossen',
    lede: 'Fünf Entscheidungen, jede getestet. Wenn du den vollen Kurs willst, sag es unten: So entscheiden wir, was wir als Nächstes bauen.',
  },

  steps: {
    tilt: {
      idea: 'Die Sonne',
      title: 'Das Modul ausrichten',
      story:
        'Ein Modul fängt am meisten Licht, wenn es zur Mittagssonne zeigt, und die Mittagssonne steht in Richtung Äquator, umso ' +
        'tiefer, je weiter man von ihm entfernt ist. Wähle eine Stadt und richte dort ein Modul aus.',
      task: 'Wähle, wohin das Modul zeigt und wie stark es geneigt ist. Fange mindestens 97 % dessen ein, was diese Stadt bestenfalls hergibt.',
      debrief:
        'Eine gute Regel: zum Äquator zeigen und etwa um den Breitengrad neigen. In Kairo heißt das Süden bei 30°, in Sydney ' +
        'Norden, in Nairobi am Äquator fast flach. Probier eine andere Stadt: Die Regel hält.',
      leavesOut: 'Wolken, Staub, Hitze (Module verlieren Leistung, wenn sie heiß werden) und Schatten. Es ist ein Klarhimmelmodell, nur zum Vergleich von Winkeln.',
    },
    array: {
      idea: 'Angebot und Bedarf',
      title: 'Wie viele Module?',
      story:
        'Ein Haus in Kairo verbraucht etwa 6.100 kWh im Jahr, das meiste im Sommer für die Klimaanlage. Jedes Modul hat 400 W, ' +
        'und auf dem Dach ist Platz für 14.',
      task: 'Klicke aufs Dach, um Module zu setzen. Decke zwischen 80 % und 120 % des Jahresverbrauchs: weniger heißt hohe Rechnungen, mehr verkauft Strom für wenig Geld.',
      debrief:
        'Sieben bis zehn Module reichen. In Kairo haben Sonne und Klimaanlage gemeinsam im Sommer ihren Höhepunkt, ein Glücksfall. ' +
        'In einer kälteren Stadt laufen beide auseinander, wie der letzte Schritt zeigt.',
      leavesOut: 'Tag gegen Nacht (der nächste Schritt), trübe Tage, Module, die pro Jahr etwa ein halbes Prozent verlieren, und die Einspeisevergütung.',
    },
    battery: {
      idea: 'Speicher',
      title: 'Licht die ganze Nacht',
      story:
        'Die Sonne geht um sechs unter, und das Haus braucht zwischen Sonnenunter- und -aufgang 6 kWh, das meiste am Abend. Ein ' +
        'Speicher kann die Nacht tragen, aber nur 90 % davon sollten genutzt werden, und das Budget liegt bei 4.000 $ zu 450 $ pro kWh.',
      task: 'Wähle einen Speicher, der bis zum Sonnenaufgang reicht und ins Budget passt.',
      debrief:
        'Nur 7,5 kWh funktionieren: 6,75 nutzbar, genug für die Nacht, für 3.375 $. Speicher werden für die härteste Nacht ' +
        'ausgelegt, und die 90-%-Regel ist die eigene Sicherheitsreserve des Speichers: Ihn ganz zu leeren, verschleißt ihn schneller.',
      leavesOut: 'Winternächte, etwa ein Zehntel Verlust beim Laden und Entladen, Alterung und ein trüber Tag, der ihn nie füllt.',
    },
    cable: {
      idea: 'Leitungen',
      title: 'Das Kabel ins Haus',
      story:
        'Der Wechselrichter steht 25 m vom Hauptverteiler entfernt und schickt 25 A bei 230 V. Jedes Kabel hat einen Widerstand: Ist ' +
        'es zu dünn, wird es heiß, und die Spannung, die im Haus ankommt, sinkt. Die Regel hier: höchstens 3 % Verlust.',
      task: 'Wähle das dünnste Kupferkabel, das 25 A sicher trägt und höchstens 3 % der Spannung verliert.',
      debrief:
        '4 mm² ist das dünnste, das besteht: für 32 A ausgelegt, verliert es etwa 2,3 %. Das ist der Iron-Man-Schritt aus den ' +
        'Grundlagen im Maßstab eines Hauses: Strom heizt Leitungen und verschwendet Energie, also wird für beides bemessen.',
      leavesOut: 'Die Verlegeart (in der Wand oder frei an der Luft ändert die Belastbarkeit), die Temperatur und die Schutzeinrichtungen an beiden Enden.',
    },
    'power-ai': {
      idea: 'Die KI überprüfen',
      title: 'Iris verlegt das Dach nach Berlin',
      story: 'Eine zweite Kundin wohnt in Berlin und verbraucht etwa 3.900 kWh im Jahr, mehr im Winter. Iris hat den Kairo-Plan kopiert.',
      task: 'Teste den Plan von Iris mit den Berliner Monaten. Deckt er weniger als 80 % des Jahres, verbessere ihn mit Modulen oder Neigung.',
      debrief:
        'Iris hat die Sonne von Kairo kopiert. Ein Modul in Berlin liefert kaum mehr als die Hälfte dessen, was es in Kairo ' +
        'liefert, und der Berliner Verbrauch ist im Winter am höchsten, wenn die Sonne am tiefsten steht. Ein Plan zieht erst ' +
        'um, wenn seine Zahlen umgezogen sind.',
      leavesOut: 'Schnee auf den Modulen, Berlins graue Winter (die Monatsform ist Klarhimmel) und die steilere Neigung, die den Winter bevorzugen würde.',
    },
  },

  cities: { cairo: 'Kairo', berlin: 'Berlin', tokyo: 'Tokio', nairobi: 'Nairobi', sydney: 'Sydney' },
  compass: { N: 'N', NE: 'NO', E: 'O', SE: 'SO', S: 'S', SW: 'SW', W: 'W', NW: 'NW' },
  months: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  tilt: {
    city: 'Stadt',
    facing: 'Zeigt nach',
    tilt: 'Neigung',
    unknown: 'Drücke Prüfen, um zu sehen, wie viel Licht es fängt',
    catches: (pct: string) => `Fängt ${pct} des Bestmöglichen`,
    ok: (pct: string) => `Gut ausgerichtet: ${pct} des Besten.`,
    fail: (pct: string, target: string) => `Nur ${pct} des Besten. Ziel sind ${target}.`,
  },
  array: {
    roof: 'Dach, Platz für 14 Module',
    slot: (n: number) => `Modulplatz ${n}`,
    made: (q: string) => `Erzeugt ${q} im Jahr`,
    used: (q: string) => `verbraucht ${q}`,
    count: (n: number) => (n === 1 ? '1 Modul' : `${n} Module`),
    legend: 'Gefüllte Balken: von den Modulen erzeugt. Umrandet: vom Haus verbraucht.',
    ok: (pct: string) => `Deckt ${pct} des Jahres.`,
    short: (pct: string, min: string) => `Deckt nur ${pct}. Ziel sind mindestens ${min}.`,
    over: (pct: string, max: string) => `${pct} ist mehr, als das Haus braucht. Bleib unter ${max}.`,
    roofFull: 'Auf dem Dach ist Platz für 14.',
  },
  battery: {
    size: 'Speicher',
    usable: (q: string, share: string) => `${q} nutzbar (${share})`,
    need: (q: string) => `die Nacht braucht ${q}`,
    cost: (q: string, budget: string) => `${q} von ${budget}`,
    price: (q: string) => `${q} pro kWh`,
    ok: 'Das Licht bleibt bis zum Sonnenaufgang an, im Budget.',
    short: (h: string) => `Er ist nach ${h} leer. Das Haus wird vor der Dämmerung dunkel.`,
    budget: (cost: string, budget: string) => `Er reicht für die Nacht, aber ${cost} liegt über dem Budget von ${budget}.`,
  },
  cable: {
    size: 'Kabel',
    dropRead: (pct: string) => `Spannungsfall ${pct}`,
    drop: (pct: string) => `Im Haus fehlen ${pct} der Spannung. Die Grenze sind 3 %.`,
    rating: (q: string) => `ausgelegt für ${q}`,
    ok: (pct: string) => `Sicher, und unterwegs gehen nur ${pct} verloren.`,
    hot: (rating: string, need: string) => `Ausgelegt für ${rating}, trägt aber ${need}: Es überhitzt.`,
  },
  iris: 'Nimm den Kairo-Plan: 8 Module bei 30°. Dort hat er mehr als 90 % gedeckt, in Berlin passt das schon.',
};

/* ── Japanese ─────────────────────────────────────────────────────────────── */

export const ja: typeof en = {
  docTitle: '電力・エネルギー プレビュー · EngineerOS',
  kicker: '電力・エネルギー工学のプレビュー',
  title: '太陽光の屋根',
  lede: '一つの太陽光の屋根で5つの課題: パネルの向き、枚数、夜通しの明かり、家への送電、そして寒い街向けのIrisの案の確認。',
  honest: 'プレビューであり、モデルはわざと単純にしています。各ステップで省いていることを示します。実際の設備は資格を持つ電気工事士が設計・接続します。',
  progress: (done: number, total: number) => `${total}つ中${done}つ完了`,
  finish: {
    title: '電力・エネルギー プレビューを修了しました',
    lede: '5つの判断を、すべて試験しました。本コースを学びたいなら、下で教えてください。次に何を作るかはそれで決まります。',
  },

  steps: {
    tilt: {
      idea: '太陽',
      title: 'パネルを向ける',
      story: 'パネルは正午の太陽に向いたとき最も多く光を受けます。正午の太陽は赤道の側にあり、赤道から離れるほど低くなります。都市を選び、そこでパネルを向けてください。',
      task: 'パネルの向きと傾きを選んでください。この都市で得られる最大値の97%以上を受けること。',
      debrief:
        'よい目安: 赤道の方を向け、緯度くらい傾ける。カイロなら南に30°、シドニーなら北、赤道直下のナイロビならほぼ水平。' +
        'ほかの都市でも試してみてください。目安は当てはまります。',
      leavesOut: '雲、ほこり、熱(パネルは熱くなると出力が落ちます)、そして影。これは晴天モデルで、角度の比較にだけ使います。',
    },
    array: {
      idea: '需要と供給',
      title: 'パネルは何枚?',
      story: 'カイロの家は年に約6,100 kWhを使い、その多くは夏の冷房です。パネル1枚は400 W、屋根には14枚分の場所があります。',
      task: '屋根をクリックしてパネルを置いてください。年間使用量の80%から120%をまかなうこと。少なければ電気代が高く、多すぎれば安く売るだけです。',
      debrief: '7枚から10枚で足ります。カイロでは日差しと冷房が夏にそろってピークになる、幸運な組み合わせです。寒い都市では両者が離れます。最後のステップで見てみましょう。',
      leavesOut: '昼と夜(次のステップ)、曇りの日、年に約0.5%ずつ劣化するパネル、そして売電価格。',
    },
    battery: {
      idea: '蓄電',
      title: '夜通し明かりをつける',
      story:
        '日は6時に沈み、家は日没から日の出までに6 kWhを使います。多くは夕方です。蓄電池で夜をまかなえますが、使ってよいのは容量の90%まで。' +
        '予算は4,000ドルで、1 kWhあたり450ドルです。',
      task: '日の出までもち、予算に収まる蓄電池を選んでください。',
      debrief:
        '7.5 kWhだけがうまくいきます。使えるのは6.75で夜には足り、価格は3,375ドル。蓄電池はいちばん厳しい夜に合わせて選び、' +
        '90%のルールは蓄電池自身の安全率です。空になるまで使うと早く傷みます。',
      leavesOut: '冬の夜、充放電で失う約1割、年月による容量の低下、そして蓄電池を満たせない曇りの日。',
    },
    cable: {
      idea: '電線',
      title: '家までのケーブル',
      story:
        'インバーターは分電盤から25 m離れていて、230 Vで25 Aを送ります。どのケーブルにも抵抗があり、細すぎると熱くなり、' +
        '家に届く電圧も下がります。ここでのルールは、失うのは3%まで。',
      task: '25 Aを安全に流せて、電圧の損失が3%以下になる、いちばん細い銅のケーブルを選んでください。',
      debrief:
        '合格するいちばん細いのは4 mm²。32 Aまで流せて、損失は約2.3%。基礎のアイアンマンのステップを家の規模でやったものです。' +
        '電流は電線を熱し電力を無駄にするので、ケーブルはその両方を見て選びます。',
      leavesOut: 'ケーブルの敷き方(壁の中か空中かで許容電流が変わります)、温度、そして両端の保護装置。',
    },
    'power-ai': {
      idea: 'AIを確かめる',
      title: 'Irisが屋根をベルリンへ移す',
      story: '2人目の依頼主はベルリンに住み、年に約3,900 kWhを使います。冬のほうが多めです。Irisはカイロの案をそのまま写しました。',
      task: 'ベルリンの月ごとの数字でIrisの案を試してください。年間の80%に届かなければ、パネルか傾きで直します。',
      debrief:
        'Irisはカイロの太陽を写しました。ベルリンのパネルはカイロの半分強しか発電せず、ベルリンの使用量は太陽が最も低い冬にピークを迎えます。' +
        '案を別の都市へ移せるのは、数字を移してからです。',
      leavesOut: 'パネルの雪、ベルリンの曇った冬(月ごとの形は晴天のもの)、そして冬に有利なもっと急な傾き。',
    },
  },

  cities: { cairo: 'カイロ', berlin: 'ベルリン', tokyo: '東京', nairobi: 'ナイロビ', sydney: 'シドニー' },
  compass: { N: '北', NE: '北東', E: '東', SE: '南東', S: '南', SW: '南西', W: '西', NW: '北西' },
  months: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
  tilt: {
    city: '都市',
    facing: '向き',
    tilt: '傾き',
    unknown: '「確かめる」を押すと、受ける光の量がわかります',
    catches: (pct: string) => `最大の${pct}を受けています`,
    ok: (pct: string) => `よい向きです。最大の${pct}。`,
    fail: (pct: string, target: string) => `最大の${pct}しかありません。${target}を目指してください。`,
  },
  array: {
    roof: '屋根、パネル14枚分',
    slot: (n: number) => `パネルの場所 ${n}`,
    made: (q: string) => `年に${q}を発電`,
    used: (q: string) => `使用量 ${q}`,
    count: (n: number) => `パネル${n}枚`,
    legend: '塗りつぶしの棒はパネルの発電量、枠だけの棒は家の使用量です。',
    ok: (pct: string) => `年間の${pct}をまかなえます。`,
    short: (pct: string, min: string) => `${pct}しかまかなえません。${min}以上を目指してください。`,
    over: (pct: string, max: string) => `${pct}は家に必要な量を超えています。${max}未満にしてください。`,
    roofFull: '屋根には14枚分の場所しかありません。',
  },
  battery: {
    size: '蓄電池',
    usable: (q: string, share: string) => `使えるのは${q}(${share})`,
    need: (q: string) => `夜に必要なのは${q}`,
    cost: (q: string, budget: string) => `${q}(予算${budget})`,
    price: (q: string) => `1 kWhあたり${q}`,
    ok: '予算内で、日の出まで明かりがつきます。',
    short: (h: string) => `${h}で空になります。夜明け前に家が真っ暗になります。`,
    budget: (cost: string, budget: string) => `夜はもちますが、${cost}は予算${budget}を超えています。`,
  },
  cable: {
    size: 'ケーブル',
    dropRead: (pct: string) => `電圧降下 ${pct}`,
    drop: (pct: string) => `家では電圧が${pct}失われます。上限は3%です。`,
    rating: (q: string) => `許容 ${q}`,
    ok: (pct: string) => `安全で、途中で失うのは${pct}だけです。`,
    hot: (rating: string, need: string) => `許容${rating}のところに${need}が流れ、過熱します。`,
  },
  iris: 'カイロの案を使いましょう。パネル8枚を30°で。あちらでは90%以上をまかなえたので、ベルリンでも大丈夫です。',
};
